import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  documentPaperFor,
  type DocumentPaper,
  type DocumentPaperKind,
  type DocumentPapers,
} from '@goyone/shared';
import { api } from '../lib/api';
import { MM, paginate } from '../lib/paperPaginate';

/**
 * ورقةُ المستند المطبوع (ليتر هيد A4) — إن رُفعت في «الهوية والمستندات».
 *
 * `ready` تنتظره صفحةُ الطباعة قبل أن ترسم: وإلّا ظهرت ترويسةُ النظام لحظةً
 * ثمّ اختفت تحت الورقة، أو طُبع المستند قبل وصولها. والخطأُ في جلبها لا يوقف
 * الطباعة — يُطبع المستند بترويسة النظام كما كان.
 */
export function useDocumentPaper(kind: DocumentPaperKind): { paper: DocumentPaper | null; ready: boolean } {
  const papers = useQuery({
    queryKey: ['settings', 'papers'],
    queryFn: () => api<DocumentPapers>('/settings/papers'),
    staleTime: 5 * 60_000,
  });
  return { paper: documentPaperFor(papers.data, kind), ready: !papers.isPending };
}

/** ارتفاعُ سطر «صفحة س من ص» أسفل مساحة الكتابة، بالمليمتر. */
const FOLIO_MM = 6;

/**
 * ورقةُ المستند المطبوع: `<article class="print-page">` كما كانت بلا ورقة،
 * وبورقةٍ صفحاتُ A4 صريحةٌ مقسومةٌ بالقياس (PaperPages).
 *
 * بلا ورقة يبقى ما تحقّقنا منه: المتصفّح يقسم الصفحات بهوامش `@page`
 * ويكرّر رأسَ الجدول. والورقةُ وحدها تحتاج التقسيم اليدويّ — صورةٌ كاملة
 * في كلّ صفحةٍ وحدودُ كتابةٍ لا يتجاوزها شيء.
 */
export function PrintSheet({
  paper,
  className = 'print-page',
  style,
  children,
}: {
  paper: DocumentPaper | null;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  if (!paper) {
    return (
      <article className={className} style={style} dir="rtl">
        {children}
      </article>
    );
  }
  return <PaperPages paper={paper}>{children}</PaperPages>;
}

/**
 * المستندُ صفحاتٍ على ورقة الشركة.
 *
 * يُرسم مرّةً في حاوية قياسٍ مخفيّة بعرض مساحة الكتابة، ثمّ يوزّعه
 * `paginate` على صفحاتٍ بمقاس A4 تُبنى في `pagesRef` — كلُّ صفحةٍ صورةُ
 * الورقة كاملةً، وفوقها مساحةُ الكتابة بحدودها، ورقمُ الصفحة أسفلها. وما
 * يُرى على الشاشة هو ما يُطبع: الصفحاتُ نفسها تفصلها مسافةٌ رماديّة.
 *
 * ويُعاد التقسيم متى تغيّر القياس: بعد تحميل الخطوط والصور (ResizeObserver
 * على حاوية القياس) ومع تغيّر حجم النافذة.
 */
function PaperPages({ paper, children }: { paper: DocumentPaper; children: ReactNode }) {
  const measureRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const src = measureRef.current;
    const out = pagesRef.current;
    if (!src || !out) return;

    const render = () => {
      /* حاويةُ القياس مخفيّةٌ عند الطباعة (عرضٌ صفر): التقسيمُ القائم يبقى. */
      if (!src.offsetWidth) return;
      const avail = (297 - paper.top - paper.bottom - FOLIO_MM) * MM;
      const pages = paginate(src, avail);
      out.replaceChildren(
        ...pages.map((content, i) => {
          const sheet = document.createElement('section');
          sheet.className = 'paper-sheet';
          const bg = document.createElement('img');
          bg.className = 'paper-sheet-bg';
          bg.src = paper.imageUrl;
          bg.alt = '';
          const body = document.createElement('div');
          body.className = 'print-page has-paper paper-body';
          body.dir = 'rtl';
          body.style.top = `${paper.top}mm`;
          body.style.bottom = `${paper.bottom + FOLIO_MM}mm`;
          body.style.insetInline = `${paper.side}mm`;
          body.append(...Array.from(content.childNodes));
          const folio = document.createElement('div');
          folio.className = 'paper-folio';
          folio.style.bottom = `${paper.bottom}mm`;
          folio.style.insetInline = `${paper.side}mm`;
          folio.textContent = pages.length > 1 ? `صفحة ${i + 1} من ${pages.length}` : '';
          sheet.append(bg, body, folio);
          return sheet;
        }),
      );
      out.dataset.pages = String(pages.length);
    };

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(render);
    };

    render();
    void document.fonts?.ready.then(schedule);
    const imgs = Array.from(src.querySelectorAll('img'));
    for (const img of imgs) if (!img.complete) img.addEventListener('load', schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(src);
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', schedule);
      for (const img of imgs) img.removeEventListener('load', schedule);
    };
  });

  return (
    <>
      <div className="paper-measure-wrap" aria-hidden>
        <div
          ref={measureRef}
          className="print-page has-paper paper-body paper-measure"
          dir="rtl"
          style={{ width: `${210 - 2 * paper.side}mm` }}
        >
          {children}
        </div>
      </div>
      <div ref={pagesRef} className="paper-pages" />
    </>
  );
}
